<template>
  <InertiaHead>
    <title>Refund Policy</title>
    <meta
      name="description"
      content="Understand VibeTeams’ refund policy, including eligibility, subscription refunds, billing terms, and how refund requests are reviewed and processed."
    />
    <link rel="canonical" href="https://vibeteams.ai/refund-policy" />
  </InertiaHead>

  <div class="bg-landing relative bg-cover bg-fixed bg-center bg-no-repeat">
    <!-- Navigation -->
    <nav class="fixed top-0 z-10 w-full" :class="{ 'backdrop-blur-md': isScrolled }">
      <div class="mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8">
        <InertiaLink href="/" class="inline-block">
          <img src="@images/logo/vibeteams-logo-secondary.svg" alt="VibeTeams Logo" class="h-8" />
        </InertiaLink>
      </div>
    </nav>

    <!-- Content -->
    <div class="relative mx-auto max-w-4xl px-4 pt-[120px] pb-12 sm:px-6 lg:px-8">
      <div class="animate-fade-in space-y-8">
        <!-- Header -->
        <div class="space-y-4">
          <h1 class="font-exo-bold text-4xl text-white">Refund Policy</h1>
          <p class="text-eerie-black-200 italic">Last Updated: October 2025</p>
        </div>

        <!-- Content Sections -->
        <div class="prose max-w-none space-y-8 divide-y divide-eerie-black-400 text-gray-300 prose-invert">
          <!-- Introduction -->
          <section class="space-y-4">
            <h2 class="font-exo-bold text-2xl text-white">1. Introduction</h2>
            <p>
              This Refund Policy explains when <span class="font-segoe-bold text-white">CoreProc, Inc.</span> (“we,” “us,” or “our”) provides refunds
              for paid subscriptions to <span class="font-segoe-bold text-white">VibeTeams.ai</span> (the “Service”).
            </p>
            <p>
              Our goal is to maintain a fair balance between user satisfaction and operational sustainability, given the credit-based nature of our
              service.
            </p>
            <p>
              By subscribing to any paid plan, you agree to this Refund Policy along with our
              <InertiaLink href="/terms-of-service" class="text-navy-blue-500 hover:underline"> Terms of Service. </InertiaLink>
            </p>
          </section>

          <!-- Credit System Overview -->
          <section class="space-y-4">
            <h2 class="font-exo-bold text-2xl text-white">2. Understanding Our Credit System</h2>

            <h3 class="font-segoe-bold text-xl text-white">2.1 How Credits Work</h3>
            <p>VibeTeams operates on a <span class="font-segoe-bold text-white">credit-based system</span>, where:</p>
            <ul class="list-disc space-y-2 pl-6">
              <li>Each subscription plan includes a monthly allocation of credits (tokens).</li>
              <li>Credits are consumed when you interact with AI agents.</li>
              <li>Different AI models consume different amounts of credits based on complexity.</li>
              <li>Credits reset at the start of each billing period.</li>
              <li>Unused credits do not roll over to the next cycle.</li>
            </ul>

            <h3 class="font-segoe-bold text-xl text-white">2.2 Credit Tracking</h3>
            <p>Your account dashboard shows:</p>
            <ul class="list-disc space-y-2 pl-6">
              <li>Total credits available this billing period.</li>
              <li>Credits used and remaining.</li>
              <li>Date of your next credit reset.</li>
            </ul>
          </section>

          <!-- Refund Eligibility -->
          <section class="space-y-4">
            <h2 class="font-exo-bold text-2xl text-white">3. Refund Eligibility</h2>

            <h3 class="font-segoe-bold text-xl text-white">3.1 Primary Rule</h3>
            <p>
              Refunds are available <span class="font-segoe-bold text-white">only if no credits have been used</span> during your current billing
              period.
            </p>

            <h3 class="font-segoe-bold text-xl text-white">3.2 Full Refund Conditions</h3>
            <p>You qualify for a <span class="font-segoe-bold text-white">100% refund</span> of your current subscription payment if:</p>
            <ul class="list-disc space-y-2 pl-6">
              <li>You have used <span class="font-segoe-bold text-white">zero credits</span> in the current billing period.</li>
              <li>The refund is requested <span class="font-segoe-bold text-white">within the same billing period</span>.</li>
              <li>Your account is in good standing and complies with our Terms of Service.</li>
            </ul>

            <h3 class="font-segoe-bold text-xl text-white">3.3 Non-Refundable Conditions</h3>
            <p>Refunds are <span class="font-segoe-bold text-white">not available</span> if:</p>
            <ul class="list-disc space-y-2 pl-6">
              <li>Any credits have been used.</li>
              <li>The billing period has ended.</li>
              <li>The request concerns a previous billing period.</li>
              <li>Your account was terminated for violating our Terms of Service.</li>
              <li>The subscription was obtained fraudulently or repeatedly refunded.</li>
            </ul>

            <h3 class="font-segoe-bold text-xl text-white">3.4 Partial Billing Periods</h3>
            <p>
              If you subscribe mid-month, your billing cycle starts from that date. Refund eligibility is based on usage from your actual start date,
              not the calendar month.
            </p>
          </section>

          <!-- Refund Process -->
          <section class="space-y-4">
            <h2 class="font-exo-bold text-2xl text-white">4. How to Request a Refund</h2>

            <h3 class="font-segoe-bold text-xl text-white">4.1 Submission Steps</h3>
            <p>To request a refund:</p>
            <ol class="list-decimal space-y-2 pl-6">
              <li>Confirm you have not used any credits in your account dashboard.</li>
              <li>Email <a href="mailto:hello@vibeteams.ai" class="text-navy-blue-500 hover:underline">hello@vibeteams.ai</a> with:</li>
              <ul class="mt-2 list-disc space-y-1 pl-6">
                <li>Your account email</li>
                <li>Subscription plan</li>
                <li>Transaction details</li>
                <li>Brief reason for refund</li>
              </ul>
              <li>Our team will verify your usage and eligibility.</li>
            </ol>

            <h3 class="font-segoe-bold text-xl text-white">4.2 Processing Time</h3>
            <ul class="list-disc space-y-2 pl-6">
              <li>Review: 1–3 business days.</li>
              <li>Issuance: 5–10 business days after approval (depending on your payment method).</li>
            </ul>

            <h3 class="font-segoe-bold text-xl text-white">4.3 Refund Method</h3>
            <p>Refunds are always issued to the <span class="font-segoe-bold text-white">original payment method</span> used for the purchase.</p>
          </section>

          <!-- Subscription Cancellation -->
          <section class="space-y-4">
            <h2 class="font-exo-bold text-2xl text-white">5. Subscription Cancellation</h2>

            <h3 class="font-segoe-bold text-xl text-white">5.1 Cancelling Your Subscription</h3>
            <p>You can cancel anytime in your account settings. After cancellation:</p>
            <ul class="list-disc space-y-2 pl-6">
              <li>Your plan remains active until the end of the billing period.</li>
              <li>You retain access to premium features until expiration.</li>
              <li>Auto-renewal will be disabled.</li>
            </ul>

            <h3 class="font-segoe-bold text-xl text-white">5.2 Cancellation and Refunds</h3>
            <p>
              Cancellation does <span class="font-segoe-bold text-white">not automatically trigger a refund.</span> If you wish to receive a refund,
              you must:
            </p>
            <ul class="list-disc space-y-2 pl-6">
              <li>Cancel your subscription, then</li>
              <li>Request a refund (per Section 4), and</li>
              <li>Meet the eligibility requirements (no credits used).</li>
            </ul>

            <h3 class="font-segoe-bold text-xl text-white">5.3 Downgrades</h3>
            <p>Plan downgrades take effect at the next billing cycle. No pro-rata refunds are issued.</p>
          </section>

          <!-- Special Circumstances -->
          <section class="space-y-4">
            <h2 class="font-exo-bold text-2xl text-white">6. Special Circumstances</h2>

            <h3 class="font-segoe-bold text-xl text-white">6.1 Service Outages</h3>
            <p>In the rare event of extended outages caused by us, we may offer:</p>
            <ul class="list-disc space-y-2 pl-6">
              <li>Credit extensions or bonus credits.</li>
              <li>Case-by-case evaluation (minor or scheduled maintenance excluded).</li>
            </ul>

            <h3 class="font-segoe-bold text-xl text-white">6.2 Billing Errors</h3>
            <p>If you were incorrectly charged:</p>
            <ul class="list-disc space-y-2 pl-6">
              <li>Contact us at <a href="mailto:hello@vibeteams.ai" class="text-navy-blue-500 hover:underline">hello@vibeteams.ai</a>.</li>
              <li>Verified errors are fully refunded (not subject to usage limits).</li>
            </ul>

            <h3 class="font-segoe-bold text-xl text-white">6.3 Unauthorized Charges</h3>
            <p>If you detect unauthorized activity:</p>
            <ul class="list-disc space-y-2 pl-6">
              <li>
                Contact us immediately at <a href="mailto:hello@vibeteams.ai" class="text-navy-blue-500 hover:underline">hello@vibeteams.ai</a>.
              </li>
              <li>Change your password.</li>
              <li>Verified unauthorized charges will be refunded promptly.</li>
            </ul>

            <h3 class="font-segoe-bold text-xl text-white">6.4 Exceptional Cases</h3>
            <p>
              We may consider refunds outside these rules at our <span class="font-segoe-bold text-white">sole and reasonable discretion</span>, based
              on factors such as:
            </p>
            <ul class="list-disc space-y-2 pl-6">
              <li>Service malfunction or data loss.</li>
              <li>Account history and user standing.</li>
              <li>Extraordinary personal or technical circumstances.</li>
            </ul>
          </section>

          <!-- Third-Party Payment Providers -->
          <section class="space-y-4">
            <h2 class="font-exo-bold text-2xl text-white">7. Third-Party Payment Providers</h2>
            <p>
              All payments and refunds are processed securely through <span class="font-segoe-bold text-white">third-party payment providers</span>.
            </p>
            <p>These providers handle:</p>
            <ul class="list-disc space-y-2 pl-6">
              <li>Transaction authorization and billing.</li>
              <li>Refund issuance.</li>
              <li>Fraud prevention and tax calculation.</li>
            </ul>
            <p>Refunds are also subject to their respective processing timelines and verification procedures.</p>
          </section>

          <!-- Chargebacks -->
          <section class="space-y-4">
            <h2 class="font-exo-bold text-2xl text-white">8. Chargebacks</h2>
            <p>If you file a chargeback instead of requesting a refund through us:</p>
            <ul class="list-disc space-y-2 pl-6">
              <li>Your account may be suspended.</li>
              <li>We reserve the right to dispute unjustified chargebacks.</li>
              <li>Repeated chargebacks may result in permanent termination.</li>
            </ul>
            <p>Please contact us first to resolve billing concerns quickly and avoid unnecessary delays.</p>
          </section>

          <!-- Free Plan -->
          <section class="space-y-4">
            <h2 class="font-exo-bold text-2xl text-white">9. Free Plan Users</h2>
            <p>This policy applies only to paid plans. Free users:</p>
            <ul class="list-disc space-y-2 pl-6">
              <li>Are not charged.</li>
              <li>Cannot request refunds.</li>
              <li>May cancel at any time without penalty.</li>
            </ul>
          </section>

          <!-- Changes to This Policy -->
          <section class="space-y-4">
            <h2 class="font-exo-bold text-2xl text-white">10. Changes to This Policy</h2>
            <p>We may update this Refund Policy as needed. When we do:</p>
            <ul class="list-disc space-y-2 pl-6">
              <li>The “Last Updated” date will change.</li>
              <li>Material updates will be announced via email or in-app notice.</li>
              <li>New terms apply only to future payments or renewals.</li>
            </ul>
          </section>

          <!-- Contact Information -->
          <section class="space-y-4">
            <h2 class="font-exo-bold text-2xl text-white">11. Contact Information</h2>
            <p>For refund inquiries or billing support:</p>
            <p class="font-segoe-bold text-white">CoreProc, Inc.</p>
            <div class="grid">
              <a href="mailto:hello@vibeteams.ai" class="flex w-fit items-center gap-1 no-underline"><Mail />hello@vibeteams.ai</a>
              <a href="https://vibeteams.ai" class="flex w-fit items-center gap-1 text-navy-blue-500 hover:underline"
                ><Globe /> https://vibeteams.ai</a
              >
            </div>
            <p>
              We typically respond within <span class="font-segoe-bold text-white">1–3 business days</span>. For urgent billing issues, please include
              “URGENT” in your subject line.
            </p>
          </section>

          <!-- Abuse Prevention -->
          <section class="space-y-4">
            <h2 class="font-exo-bold text-2xl text-white">12. Abuse Prevention</h2>
            <p>To protect fairness and sustainability, we reserve the right to:</p>
            <ul class="list-disc space-y-2 pl-6">
              <li>Deny refund requests that violate this policy.</li>
              <li>Investigate repeated refund requests indicating abuse.</li>
              <li>Terminate accounts engaged in fraudulent refund behavior.</li>
            </ul>
            <p>We appreciate your understanding and cooperation in maintaining a fair ecosystem for all users.</p>
          </section>
        </div>
      </div>
    </div>

    <!-- Footer -->
    <NavFooter />
  </div>
</template>

<script lang="ts">
import NavFooter from '@/components/landing/Footer.vue';
import { Head as InertiaHead } from '@inertiajs/vue3';
import { Globe, Mail } from 'lucide-vue-next';

export default {
  components: {
    NavFooter,
    InertiaHead,
    Mail,
    Globe,
  },
  data() {
    return {
      isScrolled: false,
    };
  },
  mounted() {
    window.addEventListener('scroll', this.handleScroll);
  },
  unmounted() {
    window.removeEventListener('scroll', this.handleScroll);
  },
  methods: {
    handleScroll() {
      const scrolled = window.scrollY;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollPercent = (scrolled / (documentHeight - windowHeight)) * 100;

      this.isScrolled = scrollPercent > 1;
    },
  },
};
</script>

<style>
.bg-landing {
  background-image: url('@images/landing-page/landing-bg-asset.webp');
}
</style>
